import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Plus, RotateCcw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useDeletePackage,
  useEventPackages,
  useSavePackage,
} from './events.api';
import type { EventPackage } from './types';

/** The API's own limits, mirrored so the common mistakes never leave the client. */
const NAME_MAX = 200;
const DESCRIPTION_MAX = 1000;

/** A blank package, ready for the create form. */
const emptyPackage = (sortOrder: number) => ({
  nameEn: '',
  nameEs: '',
  descriptionEn: '',
  descriptionEs: '',
  price: 0,
  sortOrder,
});

type Draft = ReturnType<typeof emptyPackage> & { id?: string };

/**
 * What the editor was opened on — and it outlives the closing.
 *
 * The dialog animates out over a couple of hundred milliseconds, and whatever
 * is read from here is still on screen for that whole time. Clearing it on
 * close is what made a phantom "Add package" flash over the dialog just
 * dismissed: with no draft there was no id, so the title fell back to the
 * create wording and the fields disappeared, leaving a header and two buttons.
 * So it stays until the next opening replaces it, and `nonce` — fresh on every
 * opening — is what tells the form to re-seed.
 *
 * `isFirst` is frozen here for the same reason: the list it is derived from
 * changes the moment a save lands, which is exactly when the dialog is on its
 * way out.
 */
type Editing = { nonce: number; draft: Draft; isFirst: boolean };

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
    </div>
  );
}

/**
 * The create/edit form, in its own component so that it owns the draft.
 *
 * Typing is the hot path here. With the draft held one level up, every
 * keystroke re-rendered the package list and the retire dialog alongside the
 * field being typed into; nothing outside this component reads the draft, so
 * now nothing outside it re-renders.
 */
function PackageDialog({
  eventId,
  editing,
  open,
  onOpenChange,
}: {
  eventId: string;
  editing: Editing;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const save = useSavePackage();
  const [draft, setDraft] = useState<Draft>(editing.draft);

  const set = (patch: Partial<Draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const nameMissing =
    !draft.nameEn.trim().length || !draft.nameEs.trim().length;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {draft.id ? t('events.editPackage') : t('events.addPackage')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {/* Without this, editing a price reads as changing what people
                already paid — which is the one thing it does not do. */}
            {editing.isFirst
              ? t('events.firstPackageBody')
              : t('events.packagePriceBody')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('events.name')} EN`}>
              <Input
                maxLength={NAME_MAX}
                value={draft.nameEn}
                onChange={(e) => set({ nameEn: e.target.value })}
              />
            </Field>
            <Field label={`${t('events.name')} ES`}>
              <Input
                maxLength={NAME_MAX}
                value={draft.nameEs}
                onChange={(e) => set({ nameEs: e.target.value })}
              />
            </Field>
          </div>

          <Field label={`${t('events.description')} EN`}>
            <Textarea
              rows={2}
              maxLength={DESCRIPTION_MAX}
              value={draft.descriptionEn}
              onChange={(e) => set({ descriptionEn: e.target.value })}
            />
          </Field>
          <Field label={`${t('events.description')} ES`}>
            <Textarea
              rows={2}
              maxLength={DESCRIPTION_MAX}
              value={draft.descriptionEs}
              onChange={(e) => set({ descriptionEs: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('events.price')}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.price}
                onChange={(e) => set({ price: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('events.sortOrder')}>
              <Input
                type="number"
                min={0}
                step="1"
                value={draft.sortOrder}
                onChange={(e) => set({ sortOrder: Number(e.target.value) })}
              />
            </Field>
          </div>

          {nameMissing && (
            <p className="text-destructive text-xs">
              {t('events.packageNamesRequired')}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={nameMissing || draft.price < 0 || save.isPending}
            onClick={(clickEvent) => {
              // Keep the dialog open if the save is refused.
              clickEvent.preventDefault();
              save.mutate(
                {
                  ...draft,
                  eventId,
                  descriptionEn: draft.descriptionEn || null,
                  descriptionEs: draft.descriptionEs || null,
                  // `isActive` is deliberately absent: this form never
                  // decides whether a package is on sale, and sending the
                  // field at all would retire it on a stale value.
                },
                {
                  onSuccess: () => {
                    toast.success(t('events.packageSaved'));
                    onOpenChange(false);
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('events.packageSaveFailed')
                    ),
                }
              );
            }}
          >
            {save.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('common.save')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * The packages an event sells at, on the event's own sheet.
 *
 * Retired packages stay in the list rather than disappearing: one is still
 * named on every booking that chose it, and putting it back on sale is the
 * only way back. Nothing here hard-deletes.
 */
export function EventPackagesSection({ eventId }: { eventId: string }) {
  const { t } = useTranslation();

  const packages = useEventPackages(eventId);
  const save = useSavePackage();
  const remove = useDeletePackage();

  const [editing, setEditing] = useState<Editing | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [retiring, setRetiring] = useState<EventPackage | null>(null);
  const nonce = useRef(0);

  const rows = packages.data ?? [];
  const hasActive = rows.some((row) => row.isActive);

  const openEditor = (draft: Draft) => {
    nonce.current += 1;
    setEditing({
      nonce: nonce.current,
      draft,
      isFirst: rows.length === 0 && !draft.id,
    });
    setEditorOpen(true);
  };

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('events.packageSaveFailed')
    );

  return (
    <>
      <Separator />

      <section className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t('events.packages')}
          </h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => openEditor(emptyPackage(rows.length + 1))}
          >
            <Plus className="size-3.5" />
            {t('events.addPackage')}
          </Button>
        </div>

        {packages.isPending && (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        )}

        {packages.isError && (
          <p className="text-muted-foreground py-4 text-center text-xs">
            {t('events.packagesFailed')}
          </p>
        )}

        {/* The empty state is where adding the first package is explained,
            because that is the act that changes how the event sells. */}
        {packages.isSuccess && rows.length === 0 && (
          <p className="text-muted-foreground rounded-lg border border-dashed p-3 text-center text-xs">
            {t('events.noPackagesBody')}
          </p>
        )}

        {/* Refetched after every save; the rows stay under the spinner. */}
        {rows.length > 0 && (
          <div className="relative space-y-2">
            <BusyOverlay show={packages.isFetching} />
            {rows.map((row) => (
              <div
                key={row.id}
                className={cn(
                  'flex flex-wrap items-start justify-between gap-2 rounded-lg border p-2 text-sm',
                  !row.isActive && 'opacity-60',
                  packages.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {row.nameEn}
                    {row.nameEs && row.nameEs !== row.nameEn && (
                      <span className="text-muted-foreground ml-1.5 font-normal">
                        / {row.nameEs}
                      </span>
                    )}
                  </p>
                  <p className="text-muted-foreground text-xs tabular-nums">
                    {formatCurrency(row.price)} · {t('events.sortOrder')}{' '}
                    {row.sortOrder}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                  {!row.isActive && (
                    <Badge variant="secondary">{t('events.retired')}</Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      openEditor({
                        id: row.id,
                        nameEn: row.nameEn ?? '',
                        nameEs: row.nameEs ?? '',
                        descriptionEn: row.descriptionEn ?? '',
                        descriptionEs: row.descriptionEs ?? '',
                        price: row.price,
                        sortOrder: row.sortOrder,
                      })
                    }
                  >
                    {t('events.edit')}
                  </Button>
                  {row.isActive ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRetiring(row)}
                    >
                      {t('events.retire')}
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={save.isPending}
                      onClick={() =>
                        save.mutate(
                          {
                            id: row.id,
                            eventId,
                            // Every field is overwritten by an update, so the row
                            // is sent back whole with only `isActive` changed.
                            nameEn: row.nameEn ?? '',
                            nameEs: row.nameEs ?? '',
                            descriptionEn: row.descriptionEn,
                            descriptionEs: row.descriptionEs,
                            price: row.price,
                            sortOrder: row.sortOrder,
                            // The one place a `true` is sent deliberately.
                            isActive: true,
                          },
                          {
                            onSuccess: () =>
                              toast.success(t('events.packageReactivated')),
                            onError: failed,
                          }
                        )
                      }
                    >
                      <RotateCcw className="size-3.5" />
                      {t('events.reactivate')}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Said once the event is packaged, and only then, since before that
            it is not yet true. */}
        {hasActive && (
          <p className="text-muted-foreground text-xs">
            {t('events.packagesRequiredNote')}
          </p>
        )}
      </section>

      {editing && (
        <PackageDialog
          key={editing.nonce}
          eventId={eventId}
          editing={editing}
          open={editorOpen}
          onOpenChange={setEditorOpen}
        />
      )}

      <AlertDialog
        open={retiring !== null}
        onOpenChange={(next) => {
          if (!next) setRetiring(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('events.retirePackage')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('events.retirePackageBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(clickEvent) => {
                clickEvent.preventDefault();
                if (!retiring) return;
                remove.mutate(retiring.id, {
                  onSuccess: () => {
                    toast.success(t('events.packageRetired'));
                    setRetiring(null);
                  },
                  // The server refuses to leave an event with live packaged
                  // bookings and nothing active to move them onto; its message
                  // says so, and says it better than we could.
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('events.packageSaveFailed')
                    ),
                });
              }}
            >
              {remove.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                t('events.retire')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
